import { Drawer } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import {
  CREATE_ACTIVITY,
  ACTIVITY_CALENDAR,
  ACTIVITY_CALENDAR_TYPE_CATEGORY,
  CALENDAR_VIEW,
  ACTIVITY_CALENDAR_TYPE_ACTIVITIES,
} from '../../../constants/ActivityCalendarConst';
import CreateActivity from './create-activity/create-activity-module';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityList, fetchActivityTagsList, fetchAllActivityCategories } from '../cmsSlice';
import ActivityCalendarList from './activity-calendar-list/activity-calendar-list';
import ActivityCalendarView from './activity-view/activity-calendar-view';
import { deleteActivityApi, handleDeactivateApi } from '../../../utils/activityCalendarAPIUtils';
import { notificationManager } from '../../../utils/NotificationUtils';
import { fetchAllLocations, selectedHotelSelector } from '../../property/propertySlice';
import LinearProgressBar from '../../../shared/progress-bar/Progress';
import { decryptLocalStorage } from '../../../utils/authUtils';
import { CALENDAR } from '../../../utils/Constants';
import moment from 'moment';
import DeletePopupModel from '../../../shared/delete-popup/Delete-Popup';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../utils/loaderSlice';

const ActivityCalendar = (props) => {
  const { permissions, showLoader = () => {} } = props;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const getAlllocationData = useSelector(({ property }) => property?.allLocations);
  const [drawerContentType, setDrawerContentType] = useState('');
  const [listTab, setListTab] = useState(ACTIVITY_CALENDAR_TYPE_CATEGORY);
  const [activeTab, setActiveTab] = useState(ACTIVITY_CALENDAR);
  const [activityData, setActivityData] = useState({});
  const [isEditValue, setIsEditValue] = useState('');
  const activityList = useSelector(({ cms }) => cms?.activityList);
  const hotelId = useSelector(selectedHotelSelector);
  const selectedLanguage = decryptLocalStorage('lang');
  const language = selectedLanguage !== 'en' ? selectedLanguage : '';
  const [viewMode, setViewMode] = useState(CALENDAR);
  const dispatch = useDispatch();
  const [activitiesList, setActivitiesList] = useState([]);
  const [calendarView, setCalendarView] = useState(CALENDAR_VIEW[2]?.id);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  useEffect(() => {
    handleGetActivityList();
  }, [hotelId, language]);

  useEffect(() => {
    if (listTab === ACTIVITY_CALENDAR_TYPE_ACTIVITIES && activityList?.length) {
      const selectedMoment = moment(selectedDate).startOf('day');

      const filteredActivities = activityList.filter((activity) => {
        if (activity?.schedule?.alwaysActive) return true;

        const schedule = activity?.schedule;
        if (!schedule) return true;
        const type = schedule?.scheduleType;
        if (!type) return true;

        if (type === 'ONE_TIME') {
          if (!schedule?.oneTime?.date) return true;
          const date = moment(schedule?.oneTime?.date, 'DD-MM-YYYY').startOf('day');
          if (!date.isValid()) return true;
          return date.isSame(selectedMoment, 'day');
        }

        if (type === 'RECURRING') {
          const startDate = moment(schedule?.recurring?.startDate, 'DD-MM-YYYY').startOf('day');
          const endDate = moment(schedule?.recurring?.endDate, 'DD-MM-YYYY').endOf('day');
          if (startDate.isValid() && endDate.isValid()) {
            if (
              !selectedMoment.isBetween(
                startDate.clone().subtract(1, 'day'),
                endDate.clone().add(1, 'day'),
              )
            ) {
              return false;
            }
          }

          const { recurs, recursWeek = [], dates = [] } = schedule?.recurring || {};
          if (!recurs) return true;
          const selectedDay = selectedMoment.format('dddd').toUpperCase();
          const selectedDateNum = selectedMoment.date();

          if (recurs === 'DAY') return true;
          if (recurs === 'WEEK') return !recursWeek.length || recursWeek.includes(selectedDay);
          if (recurs === 'MONTH') {
            if (!dates.length || (dates.length === 1 && !dates[0])) return true;
            const dateNumbers = Array.isArray(dates)
              ? dates.flatMap((d) =>
                  d
                    .split(',')
                    .map((v) => Number(v.trim()))
                    .filter((n) => !isNaN(n)),
                )
              : [];
            if (!dateNumbers.length) return true;
            const isDateMatched = dateNumbers.includes(selectedDateNum);
            return (
              isDateMatched &&
              selectedMoment.isSameOrAfter(startDate, 'day') &&
              selectedMoment.isSameOrBefore(endDate, 'day')
            );
          }
          return true;
        }

        return true;
      });

      setActivitiesList(filteredActivities);
    }
  }, [selectedDate, activityList, listTab]);

  useEffect(() => {
    if (activityData?.id) {
      const activityUpdateData = activityList?.find((event) => event?.id === activityData?.id);
      if (activityUpdateData) {
        setActivityData(activityUpdateData);
      } else {
        handleBack();
      }
    }
  }, [activityList]);

  const switchDrawerContent = () => {
    switch (drawerContentType) {
      case CREATE_ACTIVITY:
        return (
          <CreateActivity
            handleOnClose={() => {
              setIsEditValue('');
              setDrawerIsOpen(false);
            }}
            handleGetActivityList={handleGetActivityList}
            isEditValue={isEditValue}
            handleIsEditData={() => {
              setIsEditValue('');
            }}
            onActivityUpdate={(updatedData) => {
              if (activityData?.id && updatedData) {
                setActivityData((prev) => ({ ...prev, ...updatedData }));
              }
            }}
            language={language}
            showLoader={showLoader}
            hotelId={hotelId}
            getAlllocationData={getAlllocationData}
          />
        );
      default:
        break;
    }
  };

  const handleGetActivityList = (preserveViewMode, preserveCalendarView, preserveSelectedDate) => {
    try {
      dispatch(stepUpLoaderCount());
      dispatch(fetchAllActivityCategories(hotelId, language));
      dispatch(fetchActivityList(hotelId, language));
      dispatch(fetchActivityTagsList(hotelId, language));
      dispatch(fetchAllLocations(hotelId));
    } catch (err) {
      notificationManager('danger', err?.graphQLErrors?.[0]?.message);
    } finally {
      if (preserveViewMode) setViewMode(preserveViewMode);
      if (preserveCalendarView) setCalendarView(preserveCalendarView);
      if (preserveSelectedDate) setSelectedDate(preserveSelectedDate);
      dispatch(stepDownLoaderCount());
    }
  };

  const handleView = (activityCalendarData) => {
    dispatch(stepUpLoaderCount());
    setActiveTab('');
    setActivityData({ ...activityCalendarData, viewMode, calendarView });
    setTimeout(() => {
      dispatch(stepDownLoaderCount());
    }, 400);
  };

  const handleDelete = (activity) => {
    setActivityToDelete(activity);
    setShowDeleteConfirmation(true);
  };

  const handleActivityDelete = async () => {
    const preserveViewMode = viewMode;
    const preserveCalendarView = calendarView;
    const preserveSelectedDate = selectedDate;
    try {
      dispatch(stepUpLoaderCount());
      let res = await deleteActivityApi(hotelId, activityToDelete?.id, language);
      if (res?.data?.deleteActivity?.status) {
        await handleGetActivityList(preserveViewMode, preserveCalendarView, preserveSelectedDate);
        notificationManager('success', res?.data?.deleteActivity?.message);
      }
    } catch (err) {
      if (err.graphQLErrors) {
        notificationManager('danger', err?.graphQLErrors[0]?.message);
      } else {
        notificationManager('danger', `Failed to delete activity`);
      }
    } finally {
      dispatch(stepDownLoaderCount());
      setShowDeleteConfirmation(false);
      setActivityToDelete(null);
    }
  };

  const handleDeactivate = async (id, isCurrentlyActive) => {
    const preserveViewMode = viewMode;
    const preserveCalendarView = calendarView;
    const preserveSelectedDate = selectedDate;

    try {
      dispatch(stepUpLoaderCount());
      const res = await handleDeactivateApi(hotelId, id, language);
      if (res?.data?.updateActivityStatus?.status) {
        notificationManager('success', res?.data?.updateActivityStatus?.message);
        await handleGetActivityList(preserveViewMode, preserveCalendarView, preserveSelectedDate);
      }
    } catch (err) {
      notificationManager('danger', err?.graphQLErrors?.[0]?.message);
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleBack = () => {
    setActiveTab(ACTIVITY_CALENDAR);
    if (activityData?.viewMode) {
      setViewMode(activityData?.viewMode);
    }
    if (activityData?.calendarView) {
      setCalendarView(activityData?.calendarView);
    }
  };

  return (
    <div>
      <Drawer anchor='right' open={drawerIsOpen} className='right-drawer'>
        {switchDrawerContent()}
      </Drawer>
      {activeTab === ACTIVITY_CALENDAR ? (
        <ActivityCalendarList
          viewMode={viewMode}
          setViewMode={setViewMode}
          setDrawerIsOpen={setDrawerIsOpen}
          setDrawerContentType={setDrawerContentType}
          handleView={handleView}
          handleEdit={(value) => {
            setIsEditValue(value);
            setDrawerContentType(CREATE_ACTIVITY);
            setDrawerIsOpen(true);
          }}
          handleDelete={handleDelete}
          handleDeactivate={handleDeactivate}
          permissions={permissions}
          listTab={listTab}
          setListTab={setListTab}
          activitiesList={activitiesList}
          setActivitiesList={setActivitiesList}
          calendarView={calendarView}
          setCalendarView={setCalendarView}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      ) : (
        <ActivityCalendarView
          activityData={activityData}
          getAlllocationData={getAlllocationData}
          handleBack={handleBack}
          handleEdit={(value) => {
            setIsEditValue(value);
            setDrawerContentType(CREATE_ACTIVITY);
            setDrawerIsOpen(true);
          }}
        />
      )}
      {showDeleteConfirmation && (
        <DeletePopupModel
          deletePopUp={{
            title: 'Are you sure you want to delete',
            description: 'This action cannot be reversed',
            name: activityToDelete?.name,
            status: true,
          }}
          onDelete={handleActivityDelete}
          onDeleteCancel={() => {
            setShowDeleteConfirmation(false);
            setActivityToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ActivityCalendar;
